import { useQuery } from '@tanstack/react-query';

import { Education } from '@workspace/client';

/**
 * Mirrors `BACKEND_BASE_URL` in `packages/ui/src/lib/use-client.ts`: in dev the
 * API host follows whatever host served the page, so a LAN address works with
 * no IP in `.env`; only the hostname moves, the port stays. Prod keeps the
 * literal, where the API is a genuinely different origin.
 *
 * Duplicated rather than imported because www depends on `@workspace/client`
 * and `@workspace/shadcn` only — reaching the shared constant would mean
 * adding `@workspace/ui` to this app's graph for three lines. Keep the two in
 * step.
 */
const apiUrl = new URL(import.meta.env.VITE_API_URL);
if (import.meta.env.DEV) {
  apiUrl.hostname = window.location.hostname;
}

export const client = new Education({
  baseUrl: apiUrl.origin,
});

/** Server cap is 1000 per page, so a catalogue larger than this needs paging. */
const PAGE_SIZE = 1000;

/**
 * The design has no pagination, so every page is walked and the records are
 * concatenated. Without this a catalogue larger than one page would silently
 * lose its tail.
 */
async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<{ records: T[]; hasMore: boolean }>,
): Promise<T[]> {
  const records: T[] = [];
  let page = 1;

  for (;;) {
    const result = await fetchPage(page);
    records.push(...result.records);
    if (!result.hasMore) return records;
    page += 1;
  }
}

export function useCourses() {
  return useQuery({
    queryKey: ['GET /courses'],
    queryFn: () =>
      fetchAllPages((page) =>
        client.request('GET /courses', { page, pageSize: PAGE_SIZE }),
      ).then((records) => ({ records })),
  });
}

export function useCourse(slug: string) {
  return useQuery({
    queryKey: ['GET /courses/{slug}', slug],
    queryFn: () => client.request('GET /courses/{slug}', { slug }),
    retry: false,
  });
}

export function useInstructors() {
  return useQuery({
    queryKey: ['GET /instructors'],
    queryFn: () =>
      fetchAllPages((page) =>
        client.request('GET /instructors', { page, pageSize: PAGE_SIZE }),
      ).then((records) => ({ records })),
  });
}

export function useInstructor(slug: string) {
  return useQuery({
    queryKey: ['GET /instructors/{slug}', slug],
    queryFn: () => client.request('GET /instructors/{slug}', { slug }),
    retry: false,
  });
}

export const LEVEL_LABELS = {
  INTRODUCTORY: 'Introductory',
  INTERMEDIATE: 'Intermediate',
  PROFESSIONAL: 'Professional',
} as const;

export const COURSE_TYPE_LABELS = {
  ONLINE: 'Online',
  IN_PERSON: 'In-person',
} as const;

// The generated client types these columns as non-nullable, but they are
// nullable in the database, so each formatter below takes the widened type
// and supplies the copy the design expects.

/**
 * Prices are stored in halalas so the amount charged can be compared to the
 * amount published without any rounding in between. Only display divides.
 */
export function formatPrice(price: number | null) {
  if (price === null || price === undefined) return 'TBD';

  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'SAR',
  }).format(price / 100);
}

const dayFormatter = new Intl.DateTimeFormat('en', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

export function formatStartDate(startDate: string | null) {
  if (!startDate) return 'Coming Soon';
  return dayFormatter.format(new Date(startDate));
}

/**
 * A cohort with no end date still reads as a single day, so the range collapses
 * rather than trailing an em dash into nothing.
 */
export function formatDateRange(
  startDate: string | null,
  endDate: string | null,
) {
  if (!startDate) return 'Coming Soon';
  if (!endDate) return dayFormatter.format(new Date(startDate));

  return dayFormatter.formatRange(new Date(startDate), new Date(endDate));
}

/** Null when no hours are set; the caller hides the row rather than filling it. */
export function formatDuration(durationHours: number | null) {
  return durationHours ? `${durationHours} Hours` : null;
}

export function formatLevel(level: keyof typeof LEVEL_LABELS) {
  return LEVEL_LABELS[level] ?? level;
}

export function formatCourseType(
  courseType: keyof typeof COURSE_TYPE_LABELS | null,
) {
  return courseType ? (COURSE_TYPE_LABELS[courseType] ?? courseType) : null;
}
